/*
 * Copyright (c) 2018, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import createSecureEnvironment from '@locker/near-membrane-dom';
import { trusted } from '@locker/trusted-types';

import NodeDistortions from './distortions/node';
import TextDistortions from './distortions/text';
import SlotDistortions from './distortions/slot';
import ElementDistortions from './distortions/element';
import EventDistortions from './distortions/event';
import DocumentDistortions from './distortions/document';
import WindowDistortions from './distortions/window';
import EventTargetDistortions, { addEventListenerOriginal, dispatchEventOriginal } from './distortions/event-target';
import { MapConcat, isNull, isUndefined, MapCreate, isString } from './shared/utils';

// local caches
const { createElement } = document;
const {
  prepend: originalPrepend,
  append: originalAppend,
  appendChild: originalAppendChild,
  insertBefore: originalInsertBefore,
  setAttribute: originalSetAttribute,
} = Element.prototype;
const documentBodyGetter = Reflect.getOwnPropertyDescriptor(Document.prototype, 'body')!.get!;

function defineExportedGlobal(name: PropertyKey, descriptor: PropertyDescriptor) {
  Reflect.defineProperty(window, name, descriptor);
}

function getImportedGlobal(name: PropertyKey): any {
  return window[name];
}

// TODO: make sure that these are only accessible when doing controlled evaluations
const endowments = {
  $oasisExternalDefineProperty$: {
    value: defineExportedGlobal,
  },
  $oasisExternalGetProperty$: {
    value: getImportedGlobal,
  },
};

function isScriptElement(node: Node): node is HTMLScriptElement {
  return node instanceof HTMLScriptElement;
}

const patchedAppendChild = function appendChild<T extends Node>(
  this: T,
  ...args: Parameters<typeof Element.prototype.appendChild>
): ReturnType<typeof Element.prototype.appendChild> {
  const [child] = args;
  if (isScriptElement(child)) {
    createScriptReflection(child);
    return child;
  }
  return originalAppendChild.apply(this, args) as T;
};
const patchedInsertBefore = function insertBefore<T extends Node>(
  this: Node,
  ...args: Parameters<typeof Element.prototype.insertBefore>
): ReturnType<typeof Element.prototype.insertBefore> {
  const [child] = args;
  if (isScriptElement(child)) {
    createScriptReflection(child);
    return child;
  }
  return originalInsertBefore.apply(this, args) as T;
};

const patchedAppend = function append<T extends Node>(
  this: Node,
  ...args: Parameters<typeof Element.prototype.append>
): ReturnType<typeof Element.prototype.append> {
  const [child] = args;
  if (!isString(child) && isScriptElement(child)) {
    createScriptReflection(child);
    return;
  }
  originalAppend.apply(this, args);
};

const patchedPrepend = function prepend<T extends Node>(
  this: Node,
  ...args: Parameters<typeof Element.prototype.prepend>
): ReturnType<typeof Element.prototype.prepend> {
  const [child] = args;
  if (!isString(child) && isScriptElement(child)) {
    createScriptReflection(child);
    return;
  }
  originalPrepend.apply(this, args);
};

const distortionMap = MapConcat([
  MapCreate([
    // Element & Node
    [originalAppendChild, patchedAppendChild],
    [originalInsertBefore, patchedInsertBefore],
    [originalAppend, patchedAppend],
    [originalPrepend, patchedPrepend],
    // TODO: document.* as well
  ]),
  NodeDistortions,
  TextDistortions,
  SlotDistortions,
  ElementDistortions,
  EventDistortions,
  DocumentDistortions,
  EventTargetDistortions,
  WindowDistortions,
]);
const distortionCallback = (v: any) => {
  return distortionMap.get(v) || v;
};

const ve = createSecureEnvironment(window, {
  distortionCallback,
  defaultPolicy: {
    createScript: (dirty: string) => dirty,
  },
  endowments: endowments,
  keepAlive: true,
  signSourceCallback: (sourceText: string) => trusted.createScript(sourceText),
});

const magicIframe = document.querySelector('iframe');
if (isNull(magicIframe)) {
  throw new Error(`Invalid Initialization`);
}
const magicWindow = (magicIframe as HTMLIFrameElement).contentWindow!;
const magicDocument = magicWindow.document;
const magicBody = documentBodyGetter.call(magicDocument);

// patching iframe.contentWindow getter to prevent access to the magic iframe
const contentWindowDescriptor = Reflect.getOwnPropertyDescriptor(HTMLIFrameElement.prototype, 'contentWindow')!;
const originalContentWindowGetter = contentWindowDescriptor.get!;
contentWindowDescriptor.get = function contentWindow() {
  if (this === magicIframe) {
    return null;
  }
  return originalContentWindowGetter.call(this);
};
Reflect.defineProperty(HTMLIFrameElement.prototype, 'contentWindow', contentWindowDescriptor);

// patching iframe.contentDocument getter to prevent access to the magic iframe
const contentDocumentDescriptor = Reflect.getOwnPropertyDescriptor(HTMLIFrameElement.prototype, 'contentDocument')!;
const originalContentDocumentGetter = contentDocumentDescriptor.get!;
contentDocumentDescriptor.get = function contentWindow() {
  if (this === magicIframe) {
    return null;
  }
  return originalContentDocumentGetter.call(this);
};
Reflect.defineProperty(HTMLIFrameElement.prototype, 'contentDocument', contentDocumentDescriptor);

ve.evaluate(`
    // This initialization will prevent any of these APIs to be polyfilled
    // on the blue realm that can affect this sandbox.
    [
        HTMLElement.prototype,
        Element.prototype,
        Node.prototype,
        Event.prototype,
        Document.prototype,
        EventTarget.prototype,
        MutationObserver.prototype,
        HTMLCollection.prototype,
        NodeList.prototype,
        ShadowRoot.prototype,
        HTMLSlotElement.prototype,
        Text.prototype,
    ].forEach(o => delete o.$);
`);

// remap any exported globals between the sandbox and window
function mapExportedGlobals(names: string[]) {
  names.forEach((name) => {
    ve.evaluate(`
            'use strict';
            const key = \`${name}\`;
            $oasisExternalDefineProperty$(key, {
                get() { return window[key]; },
                enumerable: true,
                configurable: true,
            });
        `);
  });
}

// remap any imported globals between the sandbox and window
function mapImportedGlobals(names: string[]) {
  names.forEach((name) => {
    ve.evaluate(`
            'use strict';
            const key = \`${name}\`;
            Object.defineProperty(window, key, {
                get() { return $oasisExternalGetProperty$(key); },
                enumerable: true,
                configurable: true,
            });
        `);
  });
}

function createScriptReflection(elm: OasisScript | HTMLScriptElement) {
  const { attributes, textContent: sourceText } = elm;
  const script = createElement.call(magicDocument, 'script');

  // carry over all oasis' attributes
  for (let i = 0, len = attributes.length; i < len; i += 1) {
    const attr = attributes.item(i);
    if (!isNull(attr) && attr.name.indexOf('on') !== 0) {
      originalSetAttribute.call(script, attr.name, attr.value);
    }
  }

  // force the nonce in case it get unshelved by the browser
  if (nonce !== '' && attributes.getNamedItem('nonce')) {
    originalSetAttribute.call(script, 'nonce', nonce);
  }

  // listen for any error events on the script element
  addEventListenerOriginal.call(script, 'error', (e: ErrorEvent) => {
    dispatchEventOriginal.call(elm, new ErrorEvent('error', e));
  });

  // listen for any load events on the script element
  addEventListenerOriginal.call(script, 'load', () => dispatchEventOriginal.call(elm, new Event('load')));

  // listen for CSP errors in oasis frame
  addEventListenerOriginal.call(magicDocument, 'securitypolicyviolation', (e: SecurityPolicyViolationEvent) => {
    dispatchEventOriginal.call(elm, new SecurityPolicyViolationEvent('securitypolicyviolation', e));
  });

  // for posterity, set any inline scripts in the script tag
  // knowing that they may not run if a src attribute was set
  if (sourceText) {
    script.textContent = sourceText;
  }

  // append the script element to the magic body
  originalAppendChild.call(magicBody, script);
}

function normalizeGlobalNames(names: string | null) {
  if (!isNull(names) && !isUndefined(names)) {
    return names
      .split(',')
      .map((name) => name.trim())
      .filter((name) => GLOBAL_NAMES_REGEX.test(name));
  }
  return [];
}

function execute(elm: OasisScript) {
  // TODO: improve this to not use an expando, use a weakmap instead
  if ((elm as any).evaluate) return; // skipping
  (elm as any).evaluate = true;
  mapExportedGlobals(normalizeGlobalNames(elm.exportedGlobalNames));
  mapImportedGlobals(normalizeGlobalNames(elm.importedGlobalNames));
  createScriptReflection(elm);
}

// disallow spaces but allow anything else, including "_", "-" and "$"
const GLOBAL_NAMES_REGEX = /^\S+$/;
let nonce = '';

class OasisScript extends HTMLElement {
  static get observedAttributes() {
    return ['nonce'];
  }

  constructor() {
    super();
    const slot = document.createElement('slot');
    addEventListenerOriginal.call(slot, 'slotchange', () => execute(this), {
      once: true, // we only care about the first time this receive some content
    });
    this.attachShadow({ mode: 'open' }).appendChild(slot);
  }
  get exportedGlobalNames() {
    return this.getAttribute('exported-global-names');
  }
  set exportedGlobalNames(v) {
    if (isNull(v) || isUndefined(v) || v === '') {
      this.removeAttribute('exported-global-names');
    } else {
      this.setAttribute('exported-global-names', v);
    }
  }
  get importedGlobalNames() {
    return this.getAttribute('imported-global-names');
  }
  set importedGlobalNames(v) {
    if (isNull(v) || isUndefined(v) || v === '') {
      this.removeAttribute('imported-global-names');
    } else {
      this.setAttribute('imported-global-names', v);
    }
  }
  get src(): string {
    return this.getAttribute('src') ?? '';
  }
  set src(v: string) {
    if (isNull(v) || isUndefined(v) || v === '') {
      this.removeAttribute('src');
    } else {
      this.setAttribute('src', v);
    }
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (name === 'nonce' && newValue !== '') {
      nonce = newValue;
    }
  }

  connectedCallback() {
    // always hide oasis element
    this.setAttribute('hidden', 'true');

    // Ensure that we only execute if there is a src
    // or if textContent has been set programmatically.
    const { src } = this;
    if (src && src.length) {
      execute(this);
    } else if (this.textContent) {
      execute(this);
    }
  }
}

customElements.define('x-oasis-script', OasisScript);

export default OasisScript;
